/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';

type UseLoanPaymentBalance = {
  loanPaymentBalance: LoanPaymentBalance;
  loadingLoanPaymentBalance: boolean;
  reloadLoanPaymentBalance: () => void;
};

const useLoanPaymentBalance = (pathName = ``): UseLoanPaymentBalance => {
  const queryClient = useQueryClient();
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const path = useMemo(() => {
    if (!pathName) {
      const year = new Date().getFullYear();
      const month = `${new Date().getMonth() + 1}`.padStart(2, '0');
      const day = `${new Date().getDate()}`.padStart(2, '0');
      return `/${year}${month}${day}`;
    }
    if (pathName.startsWith('/')) return pathName;
    return '/' + pathName;
  }, [pathName]);
  const queryKey = useMemo(() => ['loanPaymentBalance', path, token], [path, token]);

  // Fetch communication data using react-query
  const { data, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (!loanNumber || !path || !token) return null;
      try {
        const request = {
          url: `live/loan/${loanNumber}/payment/balance${path}`,
          method: 'GET',
        };
        const config = {
          signal,
        };
        const headers = {
          'Session-Token': token,
        };
        const response = await axiosCall(request, config, headers);
        console.log(`useLoanPaymentBalance.response: `, response);
        return { data: response };
      } catch (error) {
        console.error(`useLoanPaymentBalance.caught: ${error.message}`);
        return {
          data: null,
        };
      }
    },
  });

  const reloadLoanPaymentBalance = useCallback(
    () => queryClient.invalidateQueries({ queryKey }),
    [queryClient, queryKey],
  );

  return {
    loanPaymentBalance: !isLoading && data && 'data' in data && data.data ? data.data : null,
    loadingLoanPaymentBalance: isLoading,
    reloadLoanPaymentBalance,
  };
};

export default useLoanPaymentBalance;
